import styled, {keyframes } from 'styled-components';

// Keyframes for the spinner
const spin = keyframes`
  0% {transform: rotate(0deg); }
  100% {transform: rotate(360deg); }
`;

export const PageContainer = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 25px;
  background-color: #f9f9f9;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
`;

export const SectionTitle = styled.h2`
  text-align: center;
  color: #0056b3;
  margin-bottom: 30px;
  font-size: 2em;
  font-weight: 600;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 15px;
`;

export const UploadForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
  padding: 25px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
  font-size: 1.1em;
`;

export const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1em;
  color: #333;
  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

export const TextArea = styled.textarea`
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1em;
  min-height: 100px;
  resize: vertical;
  color: #333;
  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

export const Button = styled.button`
  padding: 14px 25px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1em;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  &:hover:not(:disabled) {
    background-color: #218838;
    transform: translateY(-2px);
  }
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

export const UploadStatus = styled.p`
  text-align: center;
  margin-top: 15px;
  font-size: 1em;
  color: ${props => (props.color || '#333')};
`;

export const ErrorMessage = styled.p`
  color: #dc3545;
  text-align: center;
  font-weight: bold;
  margin-top: 15px;
`;

export const LoadingMessage = styled.p`
  text-align: center;
  color: #007bff;
  margin-top: 20px;
  font-size: 1.1em;
`;

export const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); /* Adjusted min-width for better layout */
  gap: 25px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

export const PostCard = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column; /* Main direction is column for overall card */
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-5px);
  }
`;

// NEW: Wrapper for video and images to display them side-by-side
export const VideoImagesWrapper = styled.div`
  display: flex;
  gap: 10px; /* Space between video and images */
  padding: 10px;
  background-color: #f8f8f8;
  border-bottom: 1px solid #eee;
  align-items: flex-start; /* Align video and images to the top */
  flex-wrap: wrap; /* Allow wrapping on smaller screens */

  @media (max-width: 600px) {
    flex-direction: column; /* Stack video and images vertically on small screens */
    align-items: center;
  }
`;

// MODIFIED: VideoPlayer to be flexible within VideoImagesWrapper
export const VideoPlayer = styled.video`
  flex: 1; /* Allows video to take available space */
  max-width: calc(100% - 120px); /* Max width considering 100px for images + 20px gap */
  height: 200px;
  object-fit: cover;
  background-color: #000;
  border-radius: 8px;

  @media (max-width: 600px) {
    max-width: 100%; /* Full width on small screens */
    width: 100%;
    height: auto; /* Adjust height dynamically */
  }
`;

// NEW: Container for vertically aligned images
export const ImagesVerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Space between vertical images */
  
  img {
    width: 100px; /* Fixed width for images */
    height: 80px; /* Fixed height for consistency */
    object-fit: cover;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  @media (max-width: 600px) {
    flex-direction: row; /* Display images horizontally on small screens */
    width: 100%;
    justify-content: center;
    img {
      width: 48%; /* Each image takes roughly half width */
      height: 100px;
    }
  }
`;


// ImageCarousel seems redundant if ImagesVerticalContainer is used. 
export const ImageCarousel = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 10px; /* Space below images */

  img {
    flex-shrink: 0;
    width: 100%; /* Each image takes full width of the container */
    height: 150px; /* Fixed height for consistency */
    object-fit: cover;
    scroll-snap-align: start;
    border-bottom: 1px solid #eee;
  }
`;

export const PostContent = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Allow content to grow */
`;

// NEW: Post Title styled component
export const PostTitle = styled.h4`
  font-size: 1.3em;
  color: #212529;
  margin-bottom: 10px;
  font-weight: 600;
`;

export const PostDescription = styled.p`
  font-size: 0.95em;
  color: #555;
  line-height: 1.5;
  margin-bottom: 10px;
  flex-grow: 1; /* Allow description to take available space */
`;

export const PostMeta = styled.div`
  font-size: 0.8em;
  color: #888;
  margin-top: auto; /* Push meta to the bottom */
  border-top: 1px solid #eee;
  padding-top: 10px;
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  background-color: #e0e0e0;
  border-radius: 5px;
  margin-top: 10px;
  height: 10px;
`;

export const ProgressBar = styled.div<{progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: #28a745;
  border-radius: 5px;
  transition: width 0.3s ease-in-out;
`;

export const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: ${spin} 0.8s linear infinite;
  margin: 20px auto;
`;

export const NoPostsMessage = styled.p`
  text-align: center;
  font-size: 1.1em;
  color: #666;
  padding: 30px;
  grid-column: 1 / -1; /* Span across all columns */
`;